import type { GreyImage } from './image-trace';

/**
 * Making an uploaded picture into what the tracer reads — scaled, turned, in shades of grey — the
 * same on the page and in the background thread, so it does not matter which one did it.
 *
 * Only the arithmetic is here; each side draws with the canvas it has.
 */

/**
 * Longest side the tracer works at: enough for a door swing, a window and a desk on a large
 * floor to be tens of pixels across, while detail beyond it is pen texture, not plan.
 */
export const TRACE_LONGEST_SIDE = 1600;

export interface Framing {
  /** The picture's size once scaled, before it is turned. */
  readonly drawWidth: number;
  readonly drawHeight: number;
  /** The canvas it is drawn on, large enough to keep every corner once turned. */
  readonly width: number;
  readonly height: number;
  readonly radians: number;
}

/** How a `width` by `height` picture is drawn to be read: at most `longestSide` across, turned `degrees` clockwise. */
export const framingOf = (width: number, height: number, longestSide = TRACE_LONGEST_SIDE, degrees = 0): Framing => {
  const factor = Math.min(1, longestSide / Math.max(width, height));
  const drawWidth = Math.max(1, Math.round(width * factor));
  const drawHeight = Math.max(1, Math.round(height * factor));
  const radians = (degrees * Math.PI) / 180;
  const [cos, sin] = [Math.abs(Math.cos(radians)), Math.abs(Math.sin(radians))];

  return {
    drawWidth,
    drawHeight,
    width: Math.max(1, Math.round((drawWidth * cos) + (drawHeight * sin))),
    height: Math.max(1, Math.round((drawWidth * sin) + (drawHeight * cos))),
    radians,
  };
};

/** The drawing calls both canvases have, the page's and the background thread's. */
interface Drawing2d {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect: (x: number, y: number, width: number, height: number) => void;
  translate: (x: number, y: number) => void;
  rotate: (angle: number) => void;
  drawImage: (image: CanvasImageSource, x: number, y: number, width: number, height: number) => void;
  setTransform: (a: number, b: number, c: number, d: number, e: number, f: number) => void;
}

/** `image` drawn on `context` as `framing` says: on white, so a transparent PNG — and a turn's corners — read as paper. */
export const drawFramed = (context: Drawing2d, image: CanvasImageSource, framing: Framing): void => {
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, framing.width, framing.height);
  context.translate(framing.width / 2, framing.height / 2);
  context.rotate(framing.radians);
  context.drawImage(image, -framing.drawWidth / 2, -framing.drawHeight / 2, framing.drawWidth, framing.drawHeight);
  context.setTransform(1, 0, 0, 1, 0, 0);
};

/** Canvas pixels, four bytes each, in shades of grey as the tracer weighs them. */
export const greyOfRgba = (rgba: Uint8ClampedArray, width: number, height: number): GreyImage => {
  const pixels = new Uint8ClampedArray(width * height);

  for (let index = 0; index < pixels.length; index += 1) {
    const offset = index * 4;
    pixels[index] = (0.299 * rgba[offset]) + (0.587 * rgba[offset + 1]) + (0.114 * rgba[offset + 2]);
  }

  return { width, height, pixels };
};
